import { useState } from 'react'
import { X } from 'lucide-react'
import { AgendaInteligenteController } from '../../../services/controllers/agendaInteligente'
import { useAgendaInteligente } from '../../../store/agendaInteligente/useAgendaInteligente'
import { useMerchant } from '../../../store/global/useMerchant'
import { WHATSAPP_SEND_MESSAGE_BASE } from '../../../styles/constant'
import {
  cleanPhone,
  renderAgendaMessage,
} from '../../../services/templates/agendaInteligente'
import { WhatsAppIcon } from '../../CrmPet/icons/WhatsAppIcon'


function appointmentCustomerKey(appointment) {
  return appointment.cliente.customerId ||
    cleanPhone(appointment.cliente.telefone) ||
    appointment.cliente.nome
}

function appointmentPetKey(appointment) {
  return appointment.pet.petId || appointment.pet.nome
}

function findRelatedAppointments(appointment, allAppointments) {
  if (!appointment) return []

  const customerKey = appointmentCustomerKey(appointment)
  const sameCustomer = allAppointments.filter(item => appointmentCustomerKey(item) === customerKey)
  const ordered = [
    appointment,
    ...sameCustomer.filter(item => item.appointmentId !== appointment.appointmentId),
  ]
  const uniquePets = new Map()

  ordered.forEach(item => {
    const petKey = appointmentPetKey(item)
    if (!uniquePets.has(petKey)) uniquePets.set(petKey, item)
  })

  return Array.from(uniquePets.values())
}

export function WhatsAppModal() {
  const CAgendaInteligente = AgendaInteligenteController()
  const waApptId = useAgendaInteligente(st => st.data.waApptId)
  const template = useAgendaInteligente(st => st.data.template)
  const templateStoreId = useAgendaInteligente(st => st.data.templateStoreId)
  const agendamentos = useAgendaInteligente(st => st.data.agendamentos)
  const activeTab = useAgendaInteligente(st => st.data.activeTab)
  const storeId = useMerchant(st => st.data.merchant?.id)
  const loja = useMerchant(st => st.data.merchant?.name?.storeName) || localStorage.getItem('STORE') || 'Petland'

  const allAppointments = agendamentos[activeTab] || []
  const appointment = allAppointments.find(item => item.appointmentId === waApptId)

  if (!waApptId || !appointment || !storeId || templateStoreId !== storeId) return null

  const relatedAppointments = findRelatedAppointments(appointment, allAppointments)

  return (
    <MessageForm
      appointment={appointment}
      relatedAppointments={relatedAppointments}
      template={template}
      storeName={loja}
      onOpenWhatsApp={appointmentIds => {
        if (!CAgendaInteligente.isTemplateReady(templateStoreId)) return false
        return CAgendaInteligente.markAppointmentsContacted(appointmentIds, true)
      }}
      onClose={CAgendaInteligente.closeWhatsApp}
    />
  )
}

function MessageForm({
  appointment,
  relatedAppointments,
  template,
  storeName,
  onOpenWhatsApp,
  onClose,
}) {
  const [selectedAppointmentIds, setSelectedAppointmentIds] = useState([appointment.appointmentId])
  const [mode, setMode] = useState('unica')
  const [editedMessage, setEditedMessage] = useState(null)

  const hasOtherPets = relatedAppointments.length > 1
  const chosenAppointments = relatedAppointments.filter(item =>
    selectedAppointmentIds.includes(item.appointmentId)
  )
  const cliente = appointment.cliente.nome?.split(' ')[0] || appointment.cliente.nome

  const phone = cleanPhone(appointment.cliente.telefone)
  const pets = chosenAppointments.map(item => ({
    id: item.appointmentId,
    nome: item.pet.nome,
    horario: item.horario,
    sexo: item.pet.sexo,
  }))
  const differentTimes = new Set(pets.map(pet => pet.horario)).size > 1
  const combinedTime = differentTimes
    ? pets.map(pet => `${pet.nome} às ${pet.horario}`).join(' e ')
    : pets[0]?.horario
  const combinedValue = chosenAppointments.reduce((total, item) => total + (Number(item.valor) || 0), 0)
  const generatedMessages = mode === 'individual' && chosenAppointments.length > 1
    ? chosenAppointments.map(item => (
      renderAgendaMessage(
        template,
        [{ nome: item.pet.nome, sexo: item.pet.sexo }],
        item.horario,
        cliente,
        storeName,
        item.data,
        item.valor,
      )
    ))
    : [
      renderAgendaMessage(
        template,
        pets,
        combinedTime,
        cliente,
        storeName,
        appointment.data,
        combinedValue,
      ),
    ]
  const preview = generatedMessages.join('\n\n')
  const finalMessage = editedMessage === null ? preview : editedMessage
  const whatsappUrl = phone && finalMessage.trim()
    ? WHATSAPP_SEND_MESSAGE_BASE
      .replace('[PHONE_NUMBER]', phone)
      .replace('[MESSAGE]', encodeURIComponent(finalMessage))
    : null

  const toggleAppointment = appointmentId => {
    const isSelected = selectedAppointmentIds.includes(appointmentId)

    if (isSelected && selectedAppointmentIds.length === 1) return

    const nextAppointmentIds = isSelected
      ? selectedAppointmentIds.filter(id => id !== appointmentId)
      : [...selectedAppointmentIds, appointmentId]

    setSelectedAppointmentIds(nextAppointmentIds)
    if (nextAppointmentIds.length === 1) setMode('unica')
    setEditedMessage(null)
  }

  const handleOpenWhatsApp = (event) => {
    const result = onOpenWhatsApp(chosenAppointments.map(item => item.appointmentId).filter(Boolean))
    if (result === false) {
      event.preventDefault()
      return
    }
    onClose()
  }

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', padding: 16 }}
    >
      <div
        aria-modal="true"
        role="dialog"
        aria-labelledby="send-whatsapp-title"
        onClick={event => event.stopPropagation()}
        style={{ background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', maxHeight: '92vh', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #eee' }}>
          <p id="send-whatsapp-title" style={{ fontWeight: 600, fontSize: 15, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <WhatsAppIcon size={16} /> Enviar WhatsApp para {appointment.cliente.nome}
          </p>
          <button type="button" aria-label="Fechar envio por WhatsApp" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {hasOtherPets && (
            <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fff', border: 'none', fontSize: 13, fontWeight: 500 }}>
                <span>Pets do mesmo dono</span>
                <span style={{ color: '#999', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  {relatedAppointments.length} pets
                </span>
              </div>

              <div style={{ borderTop: '1px solid #eee', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <AppointmentPetOption
                  key={appointment.appointmentId}
                  appointment={appointment}
                  checked={selectedAppointmentIds.includes(appointment.appointmentId)}
                  disabled={selectedAppointmentIds.length === 1 && selectedAppointmentIds.includes(appointment.appointmentId)}
                  onChange={() => toggleAppointment(appointment.appointmentId)}
                />

                {relatedAppointments
                  .filter(item => item.appointmentId !== appointment.appointmentId)
                  .map(item => {
                    const checked = selectedAppointmentIds.includes(item.appointmentId)

                    return (
                      <AppointmentPetOption
                        key={item.appointmentId}
                        appointment={item}
                        checked={checked}
                        disabled={selectedAppointmentIds.length === 1 && checked}
                        onChange={() => toggleAppointment(item.appointmentId)}
                      />
                    )
                  })}

                {chosenAppointments.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, paddingTop: 10, borderTop: '1px solid #eee' }}>
                    {[
                      { label: 'Mensagem única', value: 'unica' },
                      { label: 'Individual', value: 'individual' },
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setMode(option.value)
                          setEditedMessage(null)
                        }}
                        style={{ flex: 1, fontSize: 12, padding: 6, borderRadius: 6, fontWeight: 500, cursor: 'pointer', border: '1px solid', transition: 'all 0.15s', background: mode === option.value ? 'var(--color-scale-500)' : 'white', color: mode === option.value ? 'white' : '#555', borderColor: mode === option.value ? 'var(--color-scale-500)' : '#ddd' }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                Mensagem
              </p>
              {editedMessage !== null && (
                <button type="button" onClick={() => setEditedMessage(null)} style={{ fontSize: 11, color: 'var(--color-scale-500)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Restaurar original
                </button>
              )}
            </div>
            <textarea
              rows={6}
              aria-label="Mensagem de WhatsApp"
              value={editedMessage === null ? preview : editedMessage}
              onChange={event => setEditedMessage(event.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, border: '1px solid #ddd', borderRadius: 8, padding: '10px 12px', resize: 'vertical', outline: 'none', fontFamily: 'inherit', color: '#333', background: editedMessage === null ? '#f8f8f8' : '#fffef0' }}
            />
            <p style={{ fontSize: 11, color: '#aaa', margin: '4px 0 0' }}>
              {editedMessage !== null ? 'Mensagem editada - ' : ''}
              {(editedMessage === null ? preview : editedMessage).length} caracteres
            </p>
          </div>

          {!phone && (
            <p style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: '8px 12px', borderRadius: 6, margin: 0 }}>
              Cliente sem telefone cadastrado.
            </p>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid #eee', background: '#fafafa' }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 16px', fontSize: 13, color: '#666', background: 'none', border: 'none', cursor: 'pointer' }}>Cancelar</button>
          {whatsappUrl && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              onClick={handleOpenWhatsApp}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', fontSize: 13, fontWeight: 500, color: 'white', background: '#16a34a', borderRadius: 8, textDecoration: 'none' }}
            >
              <WhatsAppIcon size={14} /> Enviar por WhatsApp
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function AppointmentPetOption({ appointment, checked, disabled = false, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, opacity: disabled ? 0.75 : 1 }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ accentColor: 'var(--color-scale-500)', width: 15, height: 15 }}
      />
      <span style={{ flex: 1 }}>{appointment.pet.nome}</span>
      <span style={{ background: '#f0f0f0', color: '#666', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
        {appointment.horario}
      </span>
    </label>
  )
}
